import { yupResolver } from '@hookform/resolvers/yup';
import { FormFieldError } from '@kira/checkpoint-shared-frontend';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import * as yup from 'yup';

import CounterInput from 'components/CounterInput/CounterInput';
import FormControls from 'components/FormControls/FormControls';
import InlineForm from 'components/InlineForm/InlineForm';
import Panel from 'components/Panel/Panel';
import TextArea from 'components/TextArea/TextArea';
import { CompetencyType } from 'hooks/useGetAssessmentSteps';
import useNotification from 'hooks/useNotification';
import useUpdateCompetency from 'hooks/useUpdateCompetency';
import { t } from 'locales/i18n';

interface CompetencySettingsFormProps {
  competencyType: string;
  descriptionDefaultValue: string;
  weightDefaultValue: number;
  isLoading: boolean;
}

enum FormFields {
  DESCRIPTION = 'description',
  WEIGHT = 'weight',
}

interface FormData {
  [FormFields.DESCRIPTION]: string;
  [FormFields.WEIGHT]: number;
}

const CompetencySettingsForm = ({
  competencyType,
  descriptionDefaultValue,
  weightDefaultValue,
  isLoading,
}: CompetencySettingsFormProps) => {
  const { interviewUid = '', competencyUid = '' } = useParams();
  const { updateCompetency } = useUpdateCompetency(interviewUid);
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const FormSchema = useMemo(
    () =>
      yup.object().shape({
        [FormFields.DESCRIPTION]: yup
          .string()
          .test(
            'word-count',
            t('Competency description cannot exceed 40 words'),
            (value) => {
              if (!value) return true;
              const wordCount = value.trim().split(/\s+/).length;
              return wordCount <= 40;
            }
          ),
        [FormFields.WEIGHT]: yup.number().required(t('Weight is required')),
      }),
    [FormFields.DESCRIPTION, FormFields.WEIGHT]
  );

  const {
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    reset,
    control,
  } = useForm<FormData>({
    resolver: yupResolver(FormSchema),
    mode: 'onSubmit',
    defaultValues: {
      [FormFields.DESCRIPTION]: descriptionDefaultValue,
      [FormFields.WEIGHT]: weightDefaultValue,
    },
  });

  useEffect(() => {
    reset({
      [FormFields.DESCRIPTION]: descriptionDefaultValue,
      [FormFields.WEIGHT]: weightDefaultValue,
    });
  }, [competencyUid, descriptionDefaultValue, reset, weightDefaultValue]);

  const onSubmit = ({ description, weight }: FormData) => {
    updateCompetency(
      { competencyUid, description, weight },
      {
        onSuccess: ({ description, weight }) => {
          reset({
            [FormFields.DESCRIPTION]: description,
            [FormFields.WEIGHT]: weight,
          });
          showSuccessNotification(
            t('Success'),
            t('Competency settings updated')
          );
        },
        onError: (error) => {
          showErrorNotification(t('Error'), error.message);
        },
      }
    );
  };

  const handleCancel = () => {
    reset({
      [FormFields.DESCRIPTION]: descriptionDefaultValue,
      [FormFields.WEIGHT]: weightDefaultValue,
    });
  };

  const getCompetencyTypeString = (competencyType: string) =>
    ({
      [CompetencyType.QUESTIONS]: 'Questions',
      [CompetencyType.VIDEO]: 'Video',
      [CompetencyType.WRITTEN]: 'Written',
      [CompetencyType.PASSIVE]: 'Passive',
      [CompetencyType.VERBAL_COMMUNICATION]: 'Verbal Communication',
      [CompetencyType.WRITTEN_COMMUNICATION]: 'Written Communication',
    }[competencyType]);

  return (
    <form
      data-testid="competency-update-settings-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Panel
        isFocused={isDirty}
        title={t('Competency Settings')}
        isLoading={isLoading}
      >
        <InlineForm title={t('Competency Type')}>
          <p
            className="prose-body-md-reg text-kira-slate-900"
            data-testid="Type"
          >
            {getCompetencyTypeString(competencyType)}
          </p>
        </InlineForm>
        <InlineForm
          title={t('Description')}
          description={t(
            'Describe this competency and what it looks like in your ideal applicant. This will only be visible to your admin, staff, and reviewer users.'
          )}
        >
          <Controller
            name={FormFields.DESCRIPTION}
            control={control}
            render={({ field }) => (
              <TextArea
                id={`competency-text-area-${competencyUid}`}
                errors={errors[FormFields.DESCRIPTION]}
                {...field}
              />
            )}
          />
        </InlineForm>
        <InlineForm
          title={t('Weight')}
          description={t(
            'Choose how this competency should be weighted in overall score calculations.'
          )}
        >
          <>
            <Controller
              name={FormFields.WEIGHT}
              control={control}
              render={({ field: { onChange } }) => (
                <CounterInput
                  value={watch(FormFields.WEIGHT)}
                  increments={0.5}
                  max={5}
                  setValue={onChange}
                  suffix="x"
                />
              )}
            />
            <FormFieldError
              isVisible={!!errors[FormFields.WEIGHT]}
              message={errors[FormFields.WEIGHT]?.message ?? ''}
              className="mt-2"
            />
          </>
        </InlineForm>
        {isDirty && <FormControls isLoading={false} onCancel={handleCancel} />}
      </Panel>
    </form>
  );
};

export default CompetencySettingsForm;
